import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  IconSearch,
  IconChevronLeft,
  IconPackage,
  IconDatabase,
  IconArrowRight,
  IconLink,
  IconSquarePlus,
  IconAlertTriangle,
  IconFile
} from '@tabler/icons-react'
//@ts-ignore
import { Button } from 'dsm-react-lib'
import DataSourcesModal from '../DataSourcesModal'
import FileUploadModal from './FileUploadModal'
import DataSourcesList from './DataSourcesList'
import { useNavigate } from 'react-router-dom'
import { Row } from 'reactstrap'
import { useAgentContext } from './AgentFormContext'

interface DataSourcesProps {
  setStep: (step: 'details' | 'tools' | 'dataSources') => void
  avatarFile: File | null
  agentNameValidationRef: any
  onValidationCallback: () => void
  isValidationTriggered: boolean
}

const DataSources: React.FC<DataSourcesProps> = ({
  setStep,
  avatarFile,
  agentNameValidationRef,
  onValidationCallback,
  isValidationTriggered
}) => {
  const navigate = useNavigate()
  const [showDatasourcesList, setShowDatasourcesList] = useState(true)

  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isValid, setIsValid] = useState(false)
  const {
    isAddDataboxModalOpen,
    setIsAddDataboxModalOpen,
    editingDataboxId,
    setEditingDataboxId,
    isAddDataProductModalOpen,
    setIsAddDataProductModalOpen,
    isAddSourceModalOpen,
    setIsAddSourceModalOpen,
    mixedAssetData,
    setSelectedDataProducts,
    setSelectedDataBoxes,
    removeFromMixedAssetData,
    isDataSourcesInitialized
  } = useAgentContext()

  // Extract existing data sources from mixedAssetData - memoized so consumers relying on these
  // as effect dependencies (e.g. AddDataboxModal's data-loading effect) don't refire on every render
  const existingDataProducts = useMemo(
    () => mixedAssetData.filter((item) => item.type === 'dataproduct'),
    [mixedAssetData]
  )
  const existingDataboxes = useMemo(
    () => mixedAssetData.filter((item) => item.type === 'databox'),
    [mixedAssetData]
  )

  // Navigate to list view when data sources are added
  useEffect(() => {
    if (mixedAssetData && mixedAssetData.length > 0) {
      setShowDatasourcesList(true)
    }
  }, [mixedAssetData])

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length > 0) {
      setSelectedFiles(files)
      setIsFileUploadModalOpen(true)
    }
  }

  const validateForm = async () => {
    const isAppDataValidated = await agentNameValidationRef.current()
    setIsValid(isAppDataValidated)
    return isAppDataValidated
  }

  useEffect(() => {
    if (isValidationTriggered) {
      validateForm()
    }
  }, [isValidationTriggered])

  const handleContinue = async () => {
    const isFormValidated = await validateForm()
    onValidationCallback()

    if (isFormValidated) {
      // Just navigate to list view, don't submit yet
      setShowDatasourcesList(true)
    }
  }

  const mainContent = (
    <div className="p-2 mx-auto">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        accept=".pdf,.txt,.doc,.docx,.xls,.xlsx,.xlsm"
      />

      {/* Data Source Cards */}
      <div className="flex flex-wrap gap-6 mb-12">
        <div className="flex-1 min-w-[300px] max-w-[400px] border-2 border-theme-brand-accent rounded-xl p-1 text-center bg-theme-surface shadow-sm">
          <IconPackage size={46} className="mt-3 mb-1 text-theme-brand-accent" strokeWidth={1} />
          <h3 className="font-semibold text-theme-heading mb-1 text-2xl">Databoxes</h3>
          <p className="text-theme-subheading mb-1 leading-relaxed">
            Connect existing Team
            <br />
            or Enterprise Databoxes.
          </p>
          <Button
            label="Add databox"
            onClick={() => {
              setIsAddDataboxModalOpen(true)
            }}
            color="primary"
            icon={<IconSquarePlus size={16} />}
            className="w-auto mx-auto text-sm"
          />
        </div>

        {/* Upload Files Card */}
        {/* <div className="flex-1 min-w-[300px] max-w-[400px] border-2 border-dashed border-[var(--color-primary)] rounded-xl p-1 text-center bg-white shadow-sm">
          <IconFile size={42} color="var(--color-primary)" className="mt-3 mb-1" strokeWidth={1} />
          <h3 className="font-semibold text-heading mb-1 text-2xl">Upload files</h3>
          <p className="text-subheading mb-1 leading-relaxed">
            Drag and drop files, add a link
            <br />
            or browse from your computer.
          </p>
          <div className="flex gap-50 justify-center">
            <Button
              label="Add by link"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
              }}
              variant="outline"
              color="gray"
              icon={<IconLink size={16} />}
            />
            <Button
              label="Browse files"
              onClick={() => {
                fileInputRef.current?.click()
              }}
              color="primary"
              icon={<Upload size={16} />}
            />
          </div>
        </div> */}

        {/* Data Products Card */}
        <div className="flex-1 min-w-[300px] max-w-[400px] border-2 border-theme-brand-accent rounded-xl p-1 text-center bg-theme-surface shadow-sm">
          <IconDatabase size={42} className="mt-3 mb-1 text-theme-brand-accent" strokeWidth={1} />
          <h3 className="font-semibold text-theme-heading mb-1 text-2xl">Data products</h3>
          <p className="text-theme-subheading mb-1 leading-relaxed">
            Search and link existing data products
            <br />
            to this Agent.
          </p>
          <Button
            label="Search data products"
            onClick={() => {
              setIsAddDataProductModalOpen(true)
            }}
            color="primary"
            icon={<IconSearch size={16} />}
            className="w-auto mx-auto mb-2 text-sm"
          />
        </div>
      </div>

      {isValidationTriggered && !isValid && (
        <Row className="ms-0 me-0 pt-1 alert alert-danger" role="alert">
          <span className="text-danger pl-1">
            <IconAlertTriangle size={18} />
            &nbsp; Oops! Looks like you missed some required fields. Don`t worry, we`ve got your
            back.
            <ul>
              <li>
                <u>
                  <span className=" cursor-pointer" onClick={() => setStep('details')}>
                    Click here{' '}
                  </span>
                </u>
                to get back to the previous page and fill them up.
              </li>
            </ul>
          </span>
        </Row>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={() => navigate('/manage-agents')}
          className="flex items-center gap-50 rounded-md border theme-border-default bg-theme-surface-muted p-2.5 text-theme-heading transition-colors hover:bg-theme-hover hover:text-theme-brand-accent focus:outline-none focus:ring-1 focus:ring-theme-brand-accent">
          <IconChevronLeft size={16} />
          <span>Back to agents</span>
        </button>

        <div className="flex gap-1">
          <Button
            label="Previous step"
            onClick={() => setStep('tools')}
            variant="outline"
            className="theme-accent-outline-btn"
            icon={<IconChevronLeft size={16} />}
          />
          <Button
            label="Continue"
            iconPosition="end"
            icon={<IconArrowRight size={18} />}
            color="primary"
            onClick={handleContinue}
          />
        </div>
      </div>
    </div>
  )

  return (
    <>
      {showDatasourcesList ? (
        <DataSourcesList setStep={setStep} avatarFile={avatarFile} />
      ) : (
        mainContent
      )}
      {(isAddDataboxModalOpen || isAddDataProductModalOpen || isAddSourceModalOpen) && (
        <DataSourcesModal
          isOpen={isAddDataboxModalOpen || isAddDataProductModalOpen || isAddSourceModalOpen}
          toggle={() => {
            setIsAddDataboxModalOpen(false)
            setIsAddDataProductModalOpen(false)
            setIsAddSourceModalOpen(false)
            setEditingDataboxId(null)
          }}
          initialTab={
            isAddSourceModalOpen
              ? 'personalDatabox'
              : isAddDataboxModalOpen
              ? 'databox'
              : 'dataProducts'
          }
          initialDataboxId={editingDataboxId}
          existingDataboxes={existingDataboxes}
          addDataboxToAgent={setSelectedDataBoxes}
          removeDataboxFromAgent={removeFromMixedAssetData}
          existingDataProducts={existingDataProducts}
          addDataProductToAgent={setSelectedDataProducts}
          removeDataProductFromAgent={removeFromMixedAssetData}
          isLoadingExistingSources={!isDataSourcesInitialized}
        />
      )}
      {isFileUploadModalOpen && (
        <FileUploadModal
          isOpen={isFileUploadModalOpen}
          toggle={() => setIsFileUploadModalOpen(false)}
          preselectedFiles={selectedFiles}
        />
      )}
    </>
  )
}

export default DataSources
